import { ItemSeverity } from '../collaboration/constants/safeguarding';

/**
 * Higher number = higher urgency. Must match `@pncl/models` `AlertPriority`
 * (`packages/models` cannot import `@pncl/common-models`).
 */
export enum AlertPriority {
  LOW = 10,
  MEDIUM = 20,
  HIGH = 30,
  CRITICAL = 40
}

// Keyed by the enum so a new severity cannot be added without a priority.
const ITEM_SEVERITY_TO_ALERT_PRIORITY: Record<ItemSeverity, AlertPriority> = {
  [ItemSeverity.LOW]: AlertPriority.LOW,
  [ItemSeverity.MEDIUM]: AlertPriority.MEDIUM,
  [ItemSeverity.HIGH]: AlertPriority.HIGH,
  [ItemSeverity.CRITICAL]: AlertPriority.CRITICAL
};

/**
 * Maps a safeguarding `itemSeverity` string to {@link AlertPriority}.
 * Accepts `low` | `medium` | `high` | `critical` (any case).
 * Returns `undefined` when the value is missing or unrecognised.
 */
export function toAlertPriority(
  itemSeverity: string | undefined
): AlertPriority | undefined {
  return itemSeverity
    ? ITEM_SEVERITY_TO_ALERT_PRIORITY[
        itemSeverity.toLowerCase() as ItemSeverity
      ]
    : undefined;
}

/**
 * The item carrying the highest {@link AlertPriority} among `items` — the
 * severity an alert takes at creation, and the one its description reports.
 * Ties keep the first occurrence. Returns undefined for an empty list, or when
 * no severity is recognised.
 *
 * @param onUnrecognisedSeverity Called per item whose severity does not map, so
 *   callers with a logger can report it.
 */
export function getHighestPriorityItem<T extends { itemSeverity?: string }>(
  items: readonly T[],
  onUnrecognisedSeverity?: (itemSeverity: string | undefined) => void
): T | undefined {
  let highest: T | undefined;
  let highestPriority: AlertPriority | undefined;

  for (const item of items) {
    const priority = toAlertPriority(item.itemSeverity);

    if (priority === undefined) {
      onUnrecognisedSeverity?.(item.itemSeverity);
      continue;
    }

    if (highestPriority === undefined || priority > highestPriority) {
      highest = item;
      highestPriority = priority;
    }
  }

  return highest;
}
